import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography
} from '@dtx-company/design-system/src'
import { CodeDesign } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/CodeDesign/CodeDesign'
import { EmailField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/EmailField/EmailField'
import { FacebookField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/FacebookField/FacebookField'
import { FileField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/FileField/FileField'
import { GoogleDocField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/GoogleDocField/GoogleDocField'
import { InstagramField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/InstagramField/InstagramField'
import { LandingPageDestination } from '@dtx-company/inter-app/src/types/flowcode'
import { LocgScanDestinationType } from '../../../machines/locg/locgMachine.types'
import { LoggedOutGeneratorSection } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator.types'
import { SMSField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/SMSField/SMSField'
import {
  ScanDestinationFieldsOptions,
  ScanDestinationTitle
} from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/ScanDestination'
import { URLField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/URLField/URLField'
import { YouTubeField } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/YouTubeField/YouTubeField'
import useTranslation from 'next-translate/useTranslation'

interface AccordionSectionProps {
  expanded: LoggedOutGeneratorSection | null
  onAccordionChange: (expanded: boolean, section: LoggedOutGeneratorSection) => void
  selectedScanDestinationType: LocgScanDestinationType
}

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  expanded,
  onAccordionChange,
  selectedScanDestinationType
}) => {
  const { t } = useTranslation('logged-out-generator')

  const ScanDestinationField = {
    [LandingPageDestination.URL]: URLField,
    [LandingPageDestination.FILE]: FileField,
    [LandingPageDestination.SMS]: SMSField,
    [LandingPageDestination.EMAIL]: EmailField,
    [LandingPageDestination.INSTAGRAM]: InstagramField,
    [LandingPageDestination.FACEBOOK]: FacebookField,
    [LandingPageDestination.YOUTUBE]: YouTubeField,
    [LandingPageDestination.GOOGLE_DOC]: GoogleDocField
  }[selectedScanDestinationType]

  return (
    <>
      <Accordion
        expanded={expanded === LoggedOutGeneratorSection.ScanDestination}
        onChange={(_, expanded) =>
          onAccordionChange(expanded, LoggedOutGeneratorSection.ScanDestination)
        }
        data-testid="accordion-scan-destination"
      >
        <AccordionSummary
          defaultChecked
          aria-controls={LoggedOutGeneratorSection.ScanDestination}
          id={LoggedOutGeneratorSection.ScanDestination}
        >
          <Box
            component="fieldset"
            sx={{
              display: {
                xs: 'none',
                md: 'block'
              },
              border: 'none',
              p: 0,
              m: 0,
              legend: {
                p: 0
              }
            }}
          >
            <ScanDestinationTitle />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={4}>
            <ScanDestinationFieldsOptions />
            <ScanDestinationField />
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === LoggedOutGeneratorSection.CodeDesign}
        onChange={(_, expanded) =>
          onAccordionChange(expanded, LoggedOutGeneratorSection.CodeDesign)
        }
        data-testid="accordion-code-design"
      >
        <AccordionSummary
          defaultChecked
          aria-controls={LoggedOutGeneratorSection.CodeDesign}
          id={LoggedOutGeneratorSection.CodeDesign}
        >
          <Typography variant="headlineXSmall">
            {t('LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.label')}
            <Typography component="p" variant="labelMedium" color="text.secondary">
              {t('LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.description')}
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CodeDesign hideBackButton />
        </AccordionDetails>
      </Accordion>
    </>
  )
}
